import gql from 'graphql-tag';
import { INSPECTION_SCOPE_ITEM_FIELDS } from './inspectionScopeItems';

export const FULL_REPORT_ATTACHMENT_FIELDS = gql`
  fragment FullReportAttachmentFields on ReportAttachmentType {
    id
    reportId
    reportSystemGroupId
    itemType
    mediaType
    itemId
    s3ObjectKey
    updatedAt
    fileName
    mediaType
    attachmentUrl
    longLiveUrl
    thumbnailUrl
    metadata
    resizedUrls {
      width
      url
    }
    rotatedUrls {
      degree
      url
    }
  }
`;

export const FULL_REPORT_LOCATION_FIELDS = gql`
  fragment FullReportLocationFields on ReportLocationType {
    id
    reportId
    catalogLocationTypeId
    itemType
    itemId
    name
    groupName
    updatedAt
  }
`;

export const FULL_REPORT_LIMITATION_FIELDS = gql`
  ${FULL_REPORT_LOCATION_FIELDS}
  ${FULL_REPORT_ATTACHMENT_FIELDS}

  fragment FullReportLimitationFields on ReportLimitationType {
    id
    reportSubsystemId
    reportInformationId
    catalogLimitationId
    note
    title
    detail
    updatedAt
    reportLocations {
      ...FullReportLocationFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
  }
`;

export const FULL_REPORT_APPLIANCE_INFORMATION_FIELDS = gql`
    fragment FullReportApplianceInformationsFields on ReportApplianceInformationType {
        age
        brandId
        brandName
        createdAt
        expectedLife
        id
        modelNumber
        monthManufactured
        possibleYearsManufactured
        productFailureRiskScore
        productId
        productName
        recalls
        remainingLife
        reportId
        reportInformationId
        serialNumber
        status
        subProductId
        subProductName
        updatedAt
        yearManufactured
        warrantyCovered 
        reportAttachments {
            ...FullReportAttachmentFields
        }
    }
`;

export const FULL_REPORT_INFORMATION_FIELDS = gql`
  ${FULL_REPORT_LOCATION_FIELDS}
  ${FULL_REPORT_ATTACHMENT_FIELDS}
  ${FULL_REPORT_APPLIANCE_INFORMATION_FIELDS}

  fragment FullReportInformationsFields on ReportInformationType {
    id
    reportSubsystemId
    reportLimitationId
    catalogInformationId
    status
    note
    title
    dataName
    description
    detail
    value
    questionType
    units
    cannotAnswer
    cannotAnswerReason
    allowsOther
    requiresLocations
    requiresMedias
    requiresMediaOrientation
    requiresMediaType
    requiresDeficiencyCondition
    requiresDeficiencyCatalogDeficiencyId
    requiresLimitationCondition
    inspectionScopeItemId
    isMultiSelect
    options
    displayIndex
    tags
    updatedAt
    catalogInformation {
      id
      autoDeficiencyCondition
      autoDeficiencyMediaRequired
      visualAidTitle
      visualAidKbaUrl
      visualAidImageUrl
      description
    }
    reportLocations {
      ...FullReportLocationFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
    reportApplianceInformation {
      ...FullReportApplianceInformationsFields
    }
  }
`;

export const FULL_ORIGINAL_REPORT_DEFICIENCY_FIELDS = gql`
  ${FULL_REPORT_LOCATION_FIELDS}
  ${FULL_REPORT_ATTACHMENT_FIELDS}

  fragment FullOriginalReportDeficiencyFields on ReportDeficiencyType {
    id
    reportSubsystemId
    catalogDeficiencyId
    note
    title
    detail
    severity
    updatedAt
    reportLocations {
      ...FullReportLocationFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
  }
`;

export const FULL_REPORT_DEFICIENCY_FIELDS = gql`
  ${FULL_REPORT_LOCATION_FIELDS}
  ${FULL_REPORT_ATTACHMENT_FIELDS}
  ${FULL_ORIGINAL_REPORT_DEFICIENCY_FIELDS}

  fragment FullReportDeficiencyFields on ReportDeficiencyType {
    id
    reportSubsystemId
    reportInformationId
    catalogDeficiencyId
    note
    title
    detail
    severity
    reInspectionStatus
    recommendationTypes 
    recommendationDescription 
    updatedAt
    reportLocations {
      ...FullReportLocationFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
    originalReportDeficiency {
      ...FullOriginalReportDeficiencyFields
    }
  }
`;

export const FULL_REPORT_SUBSYSTEM_FIELDS = gql`
  ${FULL_REPORT_DEFICIENCY_FIELDS}
  ${FULL_REPORT_INFORMATION_FIELDS}
  ${FULL_REPORT_LIMITATION_FIELDS}
  ${FULL_REPORT_ATTACHMENT_FIELDS}

  fragment FullReportSubsystemFields on ReportSubsystemType {
    id
    reportSystemId
    catalogSubsystemId
    templateSubsystemIds
    status
    name
    note
    updatedAt
    displayIndex
    includeOnReport
    includeOnReportIfNotPresent
    multiUseSubsystemName
    disableNotPresent
    reportDeficiencies {
      ...FullReportDeficiencyFields
    }
    reportInformations {
      ...FullReportInformationsFields
    }
    reportLimitations {
      ...FullReportLimitationFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
  }
`;

export const FULL_REPORT_SYSTEM_FIELDS = gql`
  ${FULL_REPORT_SUBSYSTEM_FIELDS}

  fragment FullReportSystemFields on ReportSystemType {
    id
    reportId
    catalogSystemId
    reportSystemGroupId
    templateSystemIds
    name
    standardText
    updatedAt
    displayIndex
    includeOnReport
    reportSubsystems {
      ...FullReportSubsystemFields
    }
    reportAttachments {
      ...FullReportAttachmentFields
    }
  }
`;

export const REPORT_FIELDS = gql`
  fragment ReportFields on ReportType {
    id
    templateReportIds
    templateNames
    inspectionReports {
      inspection {
        id
      }
    }
    pdfDocumentUrl
    createdAt
    updatedAt
    startedAt
    publishedAt
    noDeficienciesAllowed 
    noLimitationsAllowed
    autoDeficiencyLocationRequired
    geotaggingRequired
    includePermitData
    includeWideAnglePhotos
    originalReportId
    complianceText
  }
`;

export const FULL_REPORT_FIELDS = gql`
  ${FULL_REPORT_SYSTEM_FIELDS}
  ${REPORT_FIELDS}
      
  fragment FullReportFields on ReportType {
    ...ReportFields
    reportSystems {
      ...FullReportSystemFields
    }
    reportSystemGroups {
      id
      name
    }
  }
`;

export const REPORT_WITH_INSPECTION_SCOPE_ITEM_FIELDS = gql`
  ${INSPECTION_SCOPE_ITEM_FIELDS}

  fragment ReportFieldsWithInspectionScopeItemFields on ReportType {
    id
    templateReportIds
    templateNames
    inspectionReports {
      inspection {
        id
        inspectionScopeNumber
        inspectionScopeItems {
          ...InspectionScopeItemFields
        }
      }
    }
    pdfDocumentUrl
    createdAt
    updatedAt
    startedAt
    publishedAt
    noDeficienciesAllowed 
    noLimitationsAllowed
    autoDeficiencyLocationRequired
    geotaggingRequired
    includePermitData
    includeWideAnglePhotos
    originalReportId
    complianceText
  }
`;

export const FULL_REPORT_WITH_INSPECTION_SCOPE_ITEM_FIELDS = gql`
  ${FULL_REPORT_SYSTEM_FIELDS}
  ${REPORT_WITH_INSPECTION_SCOPE_ITEM_FIELDS}

  fragment FullReportWithInspectionScopeItemFields on ReportType {
    ...ReportFieldsWithInspectionScopeItemFields
    reportSystems {
      ...FullReportSystemFields
    }
    reportSystemGroups {
      id
      name
    }
  }
`;
